// @ts-nocheck
import { Stack, Title, Text as MantineText, Code, Divider, Alert, Progress, Loader, Skeleton } from '@mantine/core';
import { IconInfoCircle, IconCheck, IconAlertTriangle, IconX } from '@tabler/icons-react';
import { Card } from '../../../components/ui';

export default function FeedbackPage() {
  return (
    <Stack gap="xl">
      {/* Page Header */}
      <Stack gap="xs">
        <Title order={2}>Feedback Components</Title>
        <MantineText c="dimmed" size="sm">
          Components for providing feedback to users - alerts, progress indicators, loaders, and notifications
        </MantineText>
      </Stack>

      {/* Alert Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Alert</Title>
            <MantineText size="sm" c="dimmed">
              Display important messages and notifications with different severity levels
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Different Variants:</MantineText>
            <Stack gap="sm">
              <Alert icon={<IconInfoCircle size={16} />} title="Info Alert" color="blue">
                This is an informational message
              </Alert>
              <Alert icon={<IconCheck size={16} />} title="Success Alert" color="green">
                Operation completed successfully
              </Alert>
              <Alert icon={<IconAlertTriangle size={16} />} title="Warning Alert" color="orange">
                Please review this warning message
              </Alert>
              <Alert icon={<IconX size={16} />} title="Error Alert" color="red">
                An error occurred while processing your request
              </Alert>
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Alert icon={<IconInfoCircle size={16} />} title="Info Alert" color="blue">
  This is an informational message
</Alert>

<Alert icon={<IconCheck size={16} />} title="Success Alert" color="green">
  Operation completed successfully
</Alert>

<Alert icon={<IconAlertTriangle size={16} />} title="Warning Alert" color="orange">
  Please review this warning message
</Alert>

<Alert icon={<IconX size={16} />} title="Error Alert" color="red">
  An error occurred while processing your request
</Alert>`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Without Icon or Title:</MantineText>
            <Alert color="blue">Simple alert without icon or title</Alert>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Alert color="blue">
  Simple alert without icon or title
</Alert>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Progress Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Progress</Title>
            <MantineText size="sm" c="dimmed">
              Display progress for ongoing operations
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Different Colors and Values:</MantineText>
            <Stack gap="md">
              <div>
                <MantineText size="xs" c="dimmed" mb={4}>
                  25% Complete
                </MantineText>
                <Progress value={25} color="blue" />
              </div>
              <div>
                <MantineText size="xs" c="dimmed" mb={4}>
                  50% Complete
                </MantineText>
                <Progress value={50} color="cyan" />
              </div>
              <div>
                <MantineText size="xs" c="dimmed" mb={4}>
                  75% Complete
                </MantineText>
                <Progress value={75} color="orange" />
              </div>
              <div>
                <MantineText size="xs" c="dimmed" mb={4}>
                  100% Complete
                </MantineText>
                <Progress value={100} color="green" />
              </div>
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Progress value={25} color="blue" />
<Progress value={50} color="cyan" />
<Progress value={75} color="orange" />
<Progress value={100} color="green" />`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Sizes and Striped:</MantineText>
            <Stack gap="md">
              <Progress value={65} size="xs" />
              <Progress value={65} size="sm" />
              <Progress value={65} size="md" />
              <Progress value={65} size="lg" striped animated />
              <Progress value={65} size="xl" striped animated />
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Progress value={65} size="xs" />
<Progress value={65} size="sm" />
<Progress value={65} size="md" />
<Progress value={65} size="lg" striped animated />
<Progress value={65} size="xl" striped animated />`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Multiple Sections:</MantineText>
            <Progress.Root size="xl">
              <Progress.Section value={35} color="cyan">
                <Progress.Label>Documents</Progress.Label>
              </Progress.Section>
              <Progress.Section value={25} color="blue">
                <Progress.Label>Photos</Progress.Label>
              </Progress.Section>
              <Progress.Section value={20} color="orange">
                <Progress.Label>Videos</Progress.Label>
              </Progress.Section>
              <Progress.Section value={15} color="pink">
                <Progress.Label>Other</Progress.Label>
              </Progress.Section>
            </Progress.Root>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Progress.Root size="xl">
  <Progress.Section value={35} color="cyan">
    <Progress.Label>Documents</Progress.Label>
  </Progress.Section>
  <Progress.Section value={25} color="blue">
    <Progress.Label>Photos</Progress.Label>
  </Progress.Section>
  <Progress.Section value={20} color="orange">
    <Progress.Label>Videos</Progress.Label>
  </Progress.Section>
</Progress.Root>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Loader Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Loader</Title>
            <MantineText size="sm" c="dimmed">
              Indicate loading state with various spinner styles
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Different Types:</MantineText>
            <Stack gap="md">
              <div>
                <MantineText size="xs" c="dimmed" mb={8}>
                  Oval (default)
                </MantineText>
                <Loader type="oval" />
              </div>
              <div>
                <MantineText size="xs" c="dimmed" mb={8}>
                  Bars
                </MantineText>
                <Loader type="bars" />
              </div>
              <div>
                <MantineText size="xs" c="dimmed" mb={8}>
                  Dots
                </MantineText>
                <Loader type="dots" />
              </div>
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Loader type="oval" />
<Loader type="bars" />
<Loader type="dots" />`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Sizes and Colors:</MantineText>
            <Stack gap="md" align="flex-start">
              <Loader size="xs" />
              <Loader size="sm" color="blue" />
              <Loader size="md" color="cyan" />
              <Loader size="lg" color="violet" />
              <Loader size="xl" color="pink" />
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Loader size="xs" />
<Loader size="sm" color="blue" />
<Loader size="md" color="cyan" />
<Loader size="lg" color="violet" />
<Loader size="xl" color="pink" />`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Skeleton Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Skeleton</Title>
            <MantineText size="sm" c="dimmed">
              Display placeholder content while data is loading
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Basic Skeletons:</MantineText>
            <Stack gap="sm">
              <Skeleton height={50} circle />
              <Skeleton height={8} radius="xl" />
              <Skeleton height={8} mt={6} radius="xl" />
              <Skeleton height={8} mt={6} width="70%" radius="xl" />
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Skeleton height={50} circle />
<Skeleton height={8} radius="xl" />
<Skeleton height={8} mt={6} radius="xl" />
<Skeleton height={8} mt={6} width="70%" radius="xl" />`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Card Skeleton Example:</MantineText>
            <Card withBorder p="md" style={{ maxWidth: 400 }}>
              <Stack gap="md">
                <Skeleton height={120} />
                <Stack gap="xs">
                  <Skeleton height={20} width="80%" />
                  <Skeleton height={16} width="60%" />
                </Stack>
                <Skeleton height={40} />
              </Stack>
            </Card>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Card withBorder p="md">
  <Stack gap="md">
    <Skeleton height={120} />
    <Stack gap="xs">
      <Skeleton height={20} width="80%" />
      <Skeleton height={16} width="60%" />
    </Stack>
    <Skeleton height={40} />
  </Stack>
</Card>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Props:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • height - Height in pixels
              <br />
              • width - Width (number for px, string for %)
              <br />
              • circle - boolean for circular skeleton
              <br />
              • radius - border radius ('xs' | 'sm' | 'md' | 'lg' | 'xl')
              <br />
              • animate - boolean to enable/disable animation (default: true)
            </MantineText>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
